import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Crown, Loader2, CreditCard, CheckCircle2, Zap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { track } from '../../lib/analytics';

const BRAND = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover', jcb: 'JCB', diners: 'Diners Club', unionpay: 'UnionPay' };
export const cardLabel = (card) => (card ? `${BRAND[card.brand] || (card.brand || 'Card')} •••• ${card.last4}` : '');

const errText = (e) => {
  const d = e?.response?.data?.detail;
  return (d && typeof d === 'object' ? d.message : d) || e?.message || 'The payment did not go through.';
};

/**
 * One-click Pro upsell: charges the Pro pass to the card saved at the trial checkout (off-session; 3-D Secure on
 * demand). Renders nothing when the student has no usable card — pass `fallback` for that case.
 * variant "card" = the big block on the trial-ended wall · "banner" = the slim strip above the regular checkout.
 */
export default function OneClickUpgrade({ variant = 'card', fallback = null, onPaid }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [paid, setPaid] = useState(false);
  const isPro = user?.subscription_status && user.subscription_status !== 'free';

  useEffect(() => {
    if (!user?.id || isPro) { setSt({ available: false }); return; }
    if (!user?.trial?.card_on_file) { setSt({ available: false }); return; }
    let alive = true;
    api.get('/api/payments/one-click').then((r) => { if (alive) setSt(r.data); }).catch(() => { if (alive) setSt({ available: false }); });
    return () => { alive = false; };
  }, [user?.id, user?.trial?.card_on_file, isPro]);

  const pay = async () => {
    setBusy(true); setErr('');
    track('one_click_upgrade_click', { variant });
    try {
      let { data } = await api.post('/api/payments/one-click');
      if (data.status === 'requires_action') {
        const stripe = await loadStripe(data.publishable_key);
        const res = await stripe.confirmCardPayment(data.client_secret, { payment_method: data.payment_method_id });
        if (res.error) throw new Error(res.error.message || 'Your bank did not approve the payment.');
        ({ data } = await api.post('/api/payments/one-click/confirm', { payment_intent_id: data.payment_intent_id }));
      }
      if (data.status === 'paid') {
        track('purchase', { value: (st?.price?.amount_minor || 0) / 100, currency: (st?.price?.currency || 'cad').toLowerCase(), plan: 'pro', method: 'one_click' });
        localStorage.removeItem('pro_checkout_pending');
        const fresh = await refreshUser();
        setPaid(true);
        onPaid?.(fresh);
        setTimeout(() => navigate(fresh?.onboarding_completed === false ? '/onboarding' : '/start-here'), 1800);
        return;
      }
      if (data.status === 'processing') { setErr('Your bank is still confirming the payment — Pro unlocks automatically the moment it clears.'); return; }
      setErr(data.message || 'The payment did not go through.');
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  if (!st) return null;
  if (!st.available) return fallback;
  const card = cardLabel(st.card);
  const price = st.price?.text || '';

  if (paid) {
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-center" data-testid="one-click-paid">
        <CheckCircle2 size={28} className="text-emerald-600 mx-auto" />
        <p className="font-bold text-emerald-900 mt-2">You're Pro — welcome aboard!</p>
        <p className="text-xs text-emerald-800 mt-1">Taking you to your Pro start page…</p>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-6" data-testid="one-click-banner">
        <div className="rounded-2xl bg-slate-900 text-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.6)]">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-amber-400/20 text-amber-300 flex items-center justify-center shrink-0"><Zap size={18} /></div>
            <div className="min-w-0">
              <p className="font-bold text-sm sm:text-base">Skip the form — pay {price} with your saved card</p>
              <p className="text-xs text-white/70 mt-0.5 flex items-center gap-1.5"><CreditCard size={12} /><span data-testid="one-click-card">{card}</span> · the one you used for the trial</p>
            </div>
          </div>
          <button type="button" onClick={pay} disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 text-slate-900 font-bold text-sm px-5 py-3 hover:bg-amber-300 disabled:opacity-60 transition-colors whitespace-nowrap"
            data-testid="one-click-pay-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Crown size={15} />}{busy ? 'Charging…' : `Upgrade to Pro · ${price}`}
          </button>
        </div>
        {err && <p className="text-xs text-rose-600 mt-2 text-center" data-testid="one-click-error">{err}</p>}
      </div>
    );
  }

  return (
    <div className="mt-7 rounded-2xl border border-amber-200 bg-amber-50/70 p-5 text-left" data-testid="one-click-card-block">
      <p className="text-[11px] font-bold tracking-[0.16em] uppercase text-amber-700">One-click upgrade</p>
      <p className="text-sm text-slate-700 mt-1">Keep everything going with <b>{st.plan_label || 'the Pro pass'}</b> — {price}, charged to the card you used for the trial.</p>
      <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1.5"><CreditCard size={12} /><span data-testid="one-click-card">{card}</span></p>
      <button type="button" onClick={pay} disabled={busy}
        className="mt-4 inline-flex items-center justify-center gap-2 w-full rounded-2xl bg-slate-900 text-white font-bold text-base px-6 py-4 hover:bg-slate-800 disabled:opacity-60 transition-colors"
        data-testid="one-click-pay-btn">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Crown size={18} className="text-amber-400" />}{busy ? 'Charging your card…' : `Upgrade to Pro now · ${price}`}
      </button>
      {err && <p className="text-xs text-rose-600 mt-2" data-testid="one-click-error">{err}</p>}
      <Link to="/checkout" className="mt-3 inline-flex text-xs font-semibold text-slate-500 hover:text-slate-900 underline underline-offset-2" data-testid="one-click-other-card-link">Use a different card</Link>
    </div>
  );
}
